import * as z from 'zod';

export const AuthorizationRequestSchema = z.object({
  login: z.string(),
  password: z.string(),
  jwt: z.boolean().optional(),
});

export const AuthorizationTokensSchema = z.object({
  token: z.string().optional(),
  jwt: z.string().optional(),
  path: z.string(),
  refresh: z.string(),
  user: z.any().optional(),
});

const LogFilterSchema = z.object({
  id: z.number().optional(),
  fields: z.array(z.any()),
});

const TrackColorSchema = z.object({
  type: z.string(),
  byMovement: z.array(
    z.object({
      key: z.string(),
      color: z.string(),
    }),
  ),
  byUniversal: z.array(
    z.object({
      key: z.string(),
      color: z.string(),
    }),
  ),
});

const MapProvidersSchema = z.object({
  track: z.string(),
  location: z.string(),
  notification: z.string(),
});

const PopUpSettingsSchema = z
  .object({
    gps: z.boolean(),
    fuel: z.boolean(),
    speed: z.boolean(),
    addres: z.boolean(),
    driver: z.boolean(),
    mileage: z.boolean().optional(),
    voltage: z.boolean(),
    tempFuel: z.boolean(),
    engineWork: z.boolean(),
    mileageCAN: z.boolean(),
    lastDataFormatted: z.boolean(),
    additionalEquipment: z.boolean(),
  })
  .catchall(z.boolean())
  .optional();

const AddressSettingsSchema = z.object({
  city: z.boolean().optional(),
  house: z.boolean().optional(),
  region: z.boolean().optional(),
  street: z.boolean().optional(),
  address: z.boolean().optional(),
  country: z.boolean().optional(),
  postcode: z.boolean().optional(),
  reduction: z.boolean().optional(),
  unParseableAddress: z.boolean().optional(),
});

const DashboardReportSchema = z
  .object({
    layout: z.array(
      z.object({
        h: z.number(),
        i: z.string(),
        w: z.number(),
        x: z.number(),
        y: z.number(),
        moved: z.boolean(),
        static: z.boolean(),
      }),
    ),
    profiles: z.array(
      z.object({
        id: z.number(),
        rules: z.array(z.any()),
        style: z.object({
          color: z.string(),
          fontSize: z.string(),
          background: z.string(),
          borderColor: z.string(),
        }),
        paramName: z.string(),
      }),
    ),
  })
  .optional();

const RulesAddressSettingsSchema = AddressSettingsSchema.extend({
  showAddress: z.boolean(),
}).optional();

const SettingsSchema = z.object({
  showDemo: z.boolean().optional(),
  logFilter: z.array(LogFilterSchema),
  trackColor: TrackColorSchema,
  mapProviders: MapProvidersSchema,
  popUpSettings: PopUpSettingsSchema,
  addressSettings: AddressSettingsSchema,
  dashboardreport: DashboardReportSchema,
  rulesAddressSettings: RulesAddressSettingsSchema,
});

const WorkspaceConfigSchema = z.object({
  id: z.number(),
  rel: z.string().optional(),
  regular: z.boolean(),
  favorite: z.boolean(),
});

const RoleSchema = z.object({
  id: z.number(),
  comment: z.string(),
  name: z.string(),
  deleted_at: z.string().nullable(),
  login_path: z.string(),
  weight: z.number(),
  slug: z.string(),
  permissions: z.array(z.string()),
  pass_jwt: z.boolean(),
});

const GroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  autocheck_id: z.number(),
  server_name: z.string(),
  parent_id: z.string().nullable(),
});

const ServerSchema = z.object({
  id: z.number(),
  host: z.string(),
  port: z.number(),
  slug: z.string(),
  fqdn: z.string(),
  volume_units: z.string(),
  is_light: z.boolean(),
});

const LimitsSchema = z.object({
  workGroup: z.object({
    vehiclesLimit: z.number(),
    vehiclesWarning: z.number(),
  }),
});

const ParamsSchema = z.object({
  forceSmsIdent: z.boolean().optional(),
  recountingDepth: z.number(),
});

const UserGroupSchema = z.object({
  deleted_at: z.string().nullable(),
  id: z.string(),
  name: z.string(),
  autocheck_id: z.number(),
  server_name: z.string(),
});

const HasPermissionSchema = z.function({
  input: [z.union([z.string(), z.array(z.string())])],
  output: z.boolean(),
});

export const AuthorizedUserSchema = z.object({
  id: z.number(),
  login: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  blocked: z.boolean(),
  blocking_comment: z.string(),
  client_name: z.string(),
  language_id: z.number(),
  position: z.string(),
  parent_id: z.number().nullable(),
  server_id: z.number(),
  autocheck_id: z.number(),
  api_token: z.any().nullable(),
  show_name: z.boolean(),
  interface_language: z.string(),
  deleted_at: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string(),
  token: z.any().nullable(),
  settings: SettingsSchema,
  mobile_settings: z.record(z.any(), z.any()),
  add_fin_id: z.string(),
  uniq_fin_id: z.string(),
  sap_id: z.string(),
  blocking_intervals: z.any().nullable(),
  reports_blocked: z.boolean(),
  uuid: z.string(),
  last_login: z.string(),
  group_id: z.any().nullable(),
  usergroup_id: z.string(),
  permissions: z.array(z.string()),
  vehiclegroup_id: z.string(),
  geozonegroup_id: z.string(),
  drivergroup_id: z.string(),
  routegroup_id: z.string(),
  server_name: z.string(),
  show_name_type: z.number(),
  is_client: z.boolean(),
  whitelabel_id: z.any().nullable(),
  support_email: z.any().nullable(),
  workspaces_config: z.array(WorkspaceConfigSchema),
  password_back: z.string().nullable(),
  max_report_period: z.number(),
  params: ParamsSchema,
  emails: z.any().nullable(),
  parent_autocheck_id: z.any().nullable(),
  roles: z.array(RoleSchema),
  role_ids: z.array(z.number()),
  usergroup: UserGroupSchema,
  server: ServerSchema,
  geozonegroup: GroupSchema,
  drivergroup: GroupSchema,
  routegroup: GroupSchema,
  vehiclegroup: GroupSchema,
  limits: LimitsSchema,
  showName: z.string(),
  sessionID: z.number(),
  userName: z.string(),
  userRole: z.number(),
  showIdentification: z.boolean(),
  vehicle_count: z.string(),
  isAdmin: z.boolean(),
  isDealer: z.boolean(),
  hasPermission: HasPermissionSchema,
});

export type AuthorizedUser = z.infer<typeof AuthorizedUserSchema>;
export type AuthorizationRequest = z.infer<typeof AuthorizationRequestSchema>;
export type AuthorizationTokens = z.infer<typeof AuthorizationTokensSchema>;
